import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Space,
  Tag,
  Popconfirm,
  Typography,
  Select,
  Switch,
  Row,
  Col,
  Alert,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  DeleteOutlined,
  EditOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { botGroupSettingsAPI, botAPI, groupAPI } from '../services/api';

const { TextArea } = Input;
const { Title, Text } = Typography;

interface GroupManagementSetting {
  id: number;
  bot_id: number;
  group_id: number;
  welcome_message: string | null;
  welcome_enabled: number;
  sensitive_words: string[];
  sensitive_action: string;
  mute_duration: number;
  transfer_contact: string | null;
  transfer_keywords: string[];
  group_rules: string | null;
}

interface BotConfig {
  id: number;
  bot_name: string;
  bot_username: string;
  bot_type: string;
}

interface GroupInfo {
  id: number;
  group_id: number;
  title: string;
  bot_id: number;
}

const WelcomeTemplates: React.FC = () => {
  const [settings, setSettings] = useState<GroupManagementSetting[]>([]);
  const [bots, setBots] = useState<BotConfig[]>([]);
  const [groups, setGroups] = useState<GroupInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<GroupManagementSetting | null>(null);
  const [filterBotId, setFilterBotId] = useState<number | undefined>(undefined);
  const [form] = Form.useForm();

  const fetchBots = async () => {
    try {
      const response = await botAPI.getAll();
      setBots(response.data);
    } catch {
      message.error('获取机器人列表失败');
    }
  };

  const fetchGroups = async (botId?: number) => {
    try {
      const response = await groupAPI.getAll(botId);
      setGroups(response.data);
    } catch {
      message.error('获取群组列表失败');
    }
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await botGroupSettingsAPI.getAll(filterBotId);
      setSettings(response.data);
    } catch {
      message.error('获取欢迎语设置失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBots(); fetchGroups(); }, []);
  useEffect(() => { fetchSettings(); }, [filterBotId]);

  const getBotName = (botId: number) => {
    const bot = bots.find(b => b.id === botId);
    return bot ? bot.bot_name : `Bot #${botId}`;
  };

  const getGroupTitle = (groupId: number) => {
    const group = groups.find(g => g.group_id === groupId || g.id === groupId);
    return group ? (group.title || `${groupId}`) : `${groupId}`;
  };

  const openModal = (item?: GroupManagementSetting) => {
    if (item) {
      setEditingItem(item);
      form.setFieldsValue({
        botId: item.bot_id,
        groupId: item.group_id,
        welcomeMessage: item.welcome_message || '',
        welcomeEnabled: item.welcome_enabled === 1,
      });
    } else {
      setEditingItem(null);
      form.resetFields();
      form.setFieldsValue({
        welcomeEnabled: true,
        welcomeMessage: '',
      });
    }
    setModalVisible(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      // Fetch existing settings first to preserve other fields
      let existingData: any = {};
      try {
        const existing = await botGroupSettingsAPI.get(values.botId, values.groupId);
        if (existing.data && existing.data.id) {
          existingData = {
            sensitiveWords: existing.data.sensitive_words || [],
            sensitiveAction: existing.data.sensitive_action || 'delete',
            muteDuration: existing.data.mute_duration ?? 60,
            transferContact: existing.data.transfer_contact || null,
            transferKeywords: existing.data.transfer_keywords || [],
            groupRules: existing.data.group_rules || null,
          };
        }
      } catch {
        // No existing settings, use defaults
      }

      await botGroupSettingsAPI.save({
        botId: values.botId,
        groupId: values.groupId,
        ...existingData,
        welcomeMessage: values.welcomeMessage || null,
        welcomeEnabled: values.welcomeEnabled,
      });

      message.success(editingItem ? '欢迎语已更新' : '欢迎语已创建');
      setModalVisible(false);
      form.resetFields();
      fetchSettings();
    } catch (error: any) {
      message.error(error.response?.data?.error || '操作失败');
    }
  };

  const handleToggleEnabled = async (item: GroupManagementSetting, enabled: boolean) => {
    try {
      let existingData: any = {};
      try {
        const existing = await botGroupSettingsAPI.get(item.bot_id, item.group_id);
        if (existing.data && existing.data.id) {
          existingData = {
            sensitiveWords: existing.data.sensitive_words || [],
            sensitiveAction: existing.data.sensitive_action || 'delete',
            muteDuration: existing.data.mute_duration ?? 60,
            transferContact: existing.data.transfer_contact || null,
            transferKeywords: existing.data.transfer_keywords || [],
            groupRules: existing.data.group_rules || null,
          };
        }
      } catch {
        // ignore
      }

      await botGroupSettingsAPI.save({
        botId: item.bot_id,
        groupId: item.group_id,
        ...existingData,
        welcomeMessage: item.welcome_message || null,
        welcomeEnabled: enabled,
      });
      message.success(enabled ? '已启用' : '已禁用');
      fetchSettings();
    } catch {
      message.error('操作失败');
    }
  };

  const handleDelete = async (item: GroupManagementSetting) => {
    try {
      // Only clear welcome fields by saving with empty welcome
      let existingData: any = {};
      try {
        const existing = await botGroupSettingsAPI.get(item.bot_id, item.group_id);
        if (existing.data && existing.data.id) {
          existingData = {
            sensitiveWords: existing.data.sensitive_words || [],
            sensitiveAction: existing.data.sensitive_action || 'delete',
            muteDuration: existing.data.mute_duration ?? 60,
            transferContact: existing.data.transfer_contact || null,
            transferKeywords: existing.data.transfer_keywords || [],
            groupRules: existing.data.group_rules || null,
          };
        }
      } catch {
        // ignore
      }

      await botGroupSettingsAPI.save({
        botId: item.bot_id,
        groupId: item.group_id,
        ...existingData,
        welcomeMessage: null,
        welcomeEnabled: false,
      });
      message.success('欢迎语已清除');
      fetchSettings();
    } catch (error: any) {
      message.error(error.response?.data?.error || '删除失败');
    }
  };

  // Only show rows that have welcome_message configured
  const welcomeSettings = settings.filter(s => s.welcome_message);

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: '机器人', dataIndex: 'bot_id', width: 140,
      render: (botId: number) => getBotName(botId),
    },
    {
      title: '群组', dataIndex: 'group_id', width: 180,
      render: (groupId: number) => (
        <span title={`ID: ${groupId}`}>{getGroupTitle(groupId)}</span>
      ),
    },
    {
      title: '欢迎语内容', dataIndex: 'welcome_message', ellipsis: true,
      render: (text: string | null) => (
        <Text ellipsis title={text || ''}>{text || '-'}</Text>
      ),
    },
    {
      title: '状态', dataIndex: 'welcome_enabled', width: 100,
      render: (enabled: number, record: GroupManagementSetting) => (
        <Switch
          checked={enabled === 1}
          onChange={(checked) => handleToggleEnabled(record, checked)}
          checkedChildren={<CheckCircleOutlined />}
          unCheckedChildren={<CloseCircleOutlined />}
        />
      ),
    },
    {
      title: '操作', width: 150, fixed: 'right' as const,
      render: (_: any, record: GroupManagementSetting) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => openModal(record)}>
            编辑
          </Button>
          <Popconfirm
            title="确定清除此群组的欢迎语吗？"
            onConfirm={() => handleDelete(record)}
            okText="确定"
            cancelText="取消"
          >
            <Button size="small" danger icon={<DeleteOutlined />}>清除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const selectedBotId = Form.useWatch('botId', form);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Title level={3}>入群欢迎语管理</Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          配置群管类型机器人的新成员入群欢迎消息，支持变量替换
        </Text>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Select
              placeholder="按机器人筛选"
              allowClear
              style={{ width: '100%' }}
              onChange={value => setFilterBotId(value)}
            >
              {bots.map(bot => (
                <Select.Option key={bot.id} value={bot.id}>
                  {bot.bot_name}
                  {bot.bot_type === 'group_management' && (
                    <Tag color="blue" style={{ marginLeft: 8, fontSize: 11 }}>群管</Tag>
                  )}
                </Select.Option>
              ))}
            </Select>
          </Col>
        </Row>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchSettings}>刷新</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
            新建欢迎语
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={welcomeSettings}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: 900 }}
      />

      <Modal
        title={editingItem ? '编辑欢迎语' : '新建欢迎语'}
        open={modalVisible}
        onCancel={() => { setModalVisible(false); form.resetFields(); }}
        footer={null}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="机器人"
                name="botId"
                rules={[{ required: true, message: '请选择机器人' }]}
              >
                <Select
                  placeholder="选择机器人"
                  disabled={!!editingItem}
                  onChange={(botId) => {
                    fetchGroups(botId);
                    form.setFieldValue('groupId', undefined);
                  }}
                >
                  {bots.map(bot => (
                    <Select.Option key={bot.id} value={bot.id}>
                      {bot.bot_name}
                      {bot.bot_type === 'group_management' && (
                        <Tag color="blue" style={{ marginLeft: 8 }}>群管</Tag>
                      )}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="群组"
                name="groupId"
                rules={[{ required: true, message: '请选择群组' }]}
              >
                <Select
                  placeholder={selectedBotId ? '选择群组' : '请先选择机器人'}
                  disabled={!!editingItem || !selectedBotId}
                  showSearch
                  optionFilterProp="children"
                >
                  {groups
                    .filter(g => !selectedBotId || g.bot_id === selectedBotId)
                    .map(g => (
                      <Select.Option key={g.group_id || g.id} value={g.group_id || g.id}>
                        {g.title || `群组 ${g.group_id || g.id}`}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Alert
            message="支持的变量"
            description={
              <div style={{ fontSize: 12, lineHeight: '22px' }}>
                <Text code>{'{username}'}</Text> 新成员用户名（@xxx 或名字） &nbsp;
                <Text code>{'{first_name}'}</Text> 新成员名字 &nbsp;
                <Text code>{'{group_title}'}</Text> 群组标题
              </div>
            }
            type="info"
            icon={<InfoCircleOutlined />}
            style={{ marginBottom: 16 }}
          />

          <Form.Item
            label="欢迎语内容"
            name="welcomeMessage"
            rules={[{ required: true, message: '请输入欢迎语内容' }]}
          >
            <TextArea
              rows={5}
              placeholder={'欢迎 {username} 加入 {group_title}！\n\n请遵守群规，友善交流。'}
            />
          </Form.Item>

          <Form.Item label="启用" name="welcomeEnabled" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingItem ? '更新' : '创建'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WelcomeTemplates;
